import { CoreQuery, PayoutStatus } from "@gen/app-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useParams } from "@tanstack/react-router";

import { InfoBanner } from "~/components/info_banner";
import { LoadingScreen } from "~/components/loading_screen";
import { SectionSideNav } from "~/components/section_side_nav";

import { PayoutBreakdown } from "../PayoutBreakdown";
import { PayoutSummary } from "../PayoutSummary";

export function PayoutDetails() {
  const { payoutId } = useParams({
    from: "/admin/payouts/$payoutId",
    select: (params) => ({
      payoutId: params.payoutId,
    }),
    shouldThrow: true,
  });

  const { data: payoutDetailsData, isFetching: isFetchingPayoutDetails } =
    CoreQuery.useAdmin_getAdminPayoutDetailsQuery(payoutId);

  if (isFetchingPayoutDetails) {
    return <LoadingScreen />;
  }

  if (!payoutDetailsData || !payoutId) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <Typography variant="body1">Payout not found.</Typography>
      </Stack>
    );
  }

  const isPayoutCompleted =
    payoutDetailsData.payout.status === PayoutStatus.Completed;

  return (
    <Box
      sx={{
        display: { xs: "block", md: "flex" },
        gap: 4.5,
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: { xs: "contents", md: "block" },
          order: { md: 1 },
          flex: { md: "0 0 200px" },
          minWidth: 0,
          alignSelf: "stretch",
        }}
      >
        <SectionSideNav
          items={[
            { id: "summary", label: "Payout" },
            { id: "transactions", label: "Transactions" },
            ...(payoutDetailsData.refunds.length > 0
              ? [{ id: "refunds", label: "Refunds" }]
              : []),
          ]}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {!isPayoutCompleted && (
          <InfoBanner
            title="Pending Final Reconciliation"
            message="The listed transactions may not accurately reflect all transactions included in this payout until it is fully completed and has arrived in your bank account."
          />
        )}
        <PayoutSummary payout={payoutDetailsData.payout} />
        <PayoutBreakdown payoutDetails={payoutDetailsData} />
      </Box>
    </Box>
  );
}
